/** Serverzustand der Servicezusagen. */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '../../lib/apiError';
import {
  createCalendar,
  createHoliday,
  createSla,
  deleteCalendar,
  deleteHoliday,
  deleteSla,
  listCalendars,
  listHolidays,
  listSlaEvents,
  listSlas,
  runSlaWatchdog,
  updateCalendar,
  updateSla,
} from './api';

export const slaKeys = {
  all: ['sla'] as const,
  calendars: ['sla', 'calendars'] as const,
  holidays: (calendarId: string) => ['sla', 'holidays', calendarId] as const,
  slas: ['sla', 'slas'] as const,
  events: (ticketId: string) => ['sla', 'events', ticketId] as const,
};

const retryUnlessPermanent = (failureCount: number, error: unknown) =>
  !(error instanceof ApiError && error.isPermanent) && failureCount < 2;

/** Wie Stammdaten: selten geaendert, auf vielen Seiten gebraucht. */
const STALE_TIME = 5 * 60 * 1000;

export function useCalendars() {
  return useQuery({
    queryKey: slaKeys.calendars,
    queryFn: listCalendars,
    staleTime: STALE_TIME,
    retry: retryUnlessPermanent,
  });
}

export function useHolidays(calendarId: string | null) {
  // Der leere Schluessel und `enabled` gehoeren zusammen: Ohne Kalender wird
  // die Abfrage gar nicht ausgefuehrt, und `listHolidays('')` liefe nie.
  const key = calendarId ?? '';
  return useQuery({
    queryKey: slaKeys.holidays(key),
    queryFn: () => listHolidays(key),
    enabled: calendarId !== null,
    staleTime: STALE_TIME,
    retry: retryUnlessPermanent,
  });
}

export function useSlas() {
  return useQuery({
    queryKey: slaKeys.slas,
    queryFn: listSlas,
    staleTime: STALE_TIME,
    retry: retryUnlessPermanent,
  });
}

export function useSlaEvents(ticketId: string, enabled = true) {
  return useQuery({
    queryKey: slaKeys.events(ticketId),
    queryFn: () => listSlaEvents(ticketId),
    enabled,
    retry: retryUnlessPermanent,
  });
}

/**
 * Nach jeder Aenderung wird der **ganze** Zweig entwertet.
 *
 * Ein neuer Vorgabekalender nimmt dem bisherigen sein Kennzeichen, und eine
 * geaenderte Zusage kann auf einen anderen Kalender zeigen. Gezielt zu
 * entwerten hiesse, diese Zusammenhaenge in der Oberflaeche nachzubauen --
 * und sie beim naechsten Feld zu vergessen.
 */
function useSlaMutation<TVariables, TResult>(
  mutationFn: (variables: TVariables) => Promise<TResult>,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: slaKeys.all });
    },
  });
}

export function useCreateCalendar() {
  return useSlaMutation((payload: Record<string, unknown>) => createCalendar(payload));
}

export function useUpdateCalendar() {
  return useSlaMutation((variables: { id: string; payload: Record<string, unknown> }) =>
    updateCalendar(variables.id, variables.payload),
  );
}

export function useDeleteCalendar() {
  return useSlaMutation((id: string) => deleteCalendar(id));
}

export function useCreateHoliday(calendarId: string) {
  return useSlaMutation((payload: Record<string, unknown>) => createHoliday(calendarId, payload));
}

export function useDeleteHoliday(calendarId: string) {
  return useSlaMutation((holidayId: string) => deleteHoliday(calendarId, holidayId));
}

export function useCreateSla() {
  return useSlaMutation((payload: Record<string, unknown>) => createSla(payload));
}

export function useUpdateSla() {
  return useSlaMutation((variables: { id: string; payload: Record<string, unknown> }) =>
    updateSla(variables.id, variables.payload),
  );
}

export function useDeleteSla() {
  return useSlaMutation((id: string) => deleteSla(id));
}

/**
 * Der Waechterlauf von Hand.
 *
 * Danach werden die Zusagen neu geholt: Der Lauf schreibt Ereignisse an die
 * Tickets, und wer ihn ausloest, sieht gleich darauf nach.
 */
export function useRunWatchdog() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: runSlaWatchdog,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: slaKeys.all });
    },
  });
}
